import React, { Component } from "react";
import ReactDOM from "react-dom";

// CSS
import "./Dropdown.scss";

// Components
import Icon from "../Icon";

/* A dropdown built out of a button and a listbox, in place of a native
 * <select>: the browser paints that one itself and will not take the panel's
 * look, so a dark form ended up with a light system menu over it.
 *
 * No search box inside the menu -- these lists are short and a field inside a
 * field is a worse control, not a better one.
 *
 * Keyboard behaviour is the native one, kept on purpose: Enter/Space/Arrow to
 * open, Up/Down to move, Home/End to jump, Enter to take, Escape to leave it
 * as it was. Typing a letter jumps to the next option starting with it, which
 * is what a <select> does and what people expect from one.
 */
let seq = 0;

class Dropdown extends Component {
  constructor(props) {
    super(props);
    seq += 1;
    this.id = `dd-${seq}`;
    this.root = React.createRef();
    this.list = React.createRef();
    this.state = {
      open: false,
      // which option the keyboard is on -- not the chosen one
      active: -1,
      // where the menu goes, in viewport coordinates
      pos: null,
      // opens upward when there is no room below
      up: false,
    };
    this.typed = "";
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.onDocDown);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onDocDown);
    this.unwatch();
    if (this.typeTimer) window.clearTimeout(this.typeTimer);
  }

  // The menu is portalled to <body>, so a click in it is not a click inside
  // this component's own tree.
  onDocDown = (e) => {
    if (!this.state.open) return;
    if (this.root.current && this.root.current.contains(e.target)) return;
    if (this.list.current && this.list.current.contains(e.target)) return;
    this.close();
  };

  watch = () => {
    // capture, so a scroll in any ancestor is caught, not just the window
    window.addEventListener("scroll", this.place, true);
    window.addEventListener("resize", this.place);
  };

  unwatch = () => {
    window.removeEventListener("scroll", this.place, true);
    window.removeEventListener("resize", this.place);
  };

  /* Measures the trigger and parks the menu against it. The menu lives at the
   * end of <body>, so these are viewport coordinates and it is immune to both
   * the stacking contexts on the way up (every field runs its own entrance
   * animation, and an animated transform makes one) and the sheet's own
   * `overflow: hidden`, which would otherwise cut the list off. */
  place = () => {
    const el = this.root.current;
    if (!el) return;

    const box = el.getBoundingClientRect();
    // an empty list still has a row in it, saying so
    const rows = Math.max(this.props.options.length, 1);
    const need = Math.min(rows * 42 + 12, 256);
    const below = window.innerHeight - box.bottom - 8;
    const up = below < need && box.top > below;

    this.setState({
      up,
      pos: {
        left: Math.round(box.left),
        width: Math.round(box.width),
        top: up ? Math.round(box.top - 6) : Math.round(box.bottom + 6),
        room: Math.max(120, Math.round(up ? box.top - 14 : below)),
      },
    });
  };

  indexOfValue = () => {
    const { options, value } = this.props;
    return options.findIndex((o) => String(o.value) === String(value));
  };

  open = () => {
    if (this.props.disabled) return;

    const at = this.indexOfValue();
    this.place();
    this.setState({ open: true, active: at >= 0 ? at : 0 }, () => {
      this.watch();
      this.scrollToActive();
    });
  };

  close = (focus) => {
    this.unwatch();
    this.setState({ open: false, active: -1, pos: null });
    if (focus && this.trigger) this.trigger.focus();
  };

  pick = (option) => {
    if (option.disabled) return;
    this.props.onChange(option.value);
    this.close(true);
  };

  scrollToActive = () => {
    const list = this.list.current;
    if (!list) return;
    const el = list.children[this.state.active];
    if (!el) return;
    if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop;
    else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight;
    }
  };

  move = (step) => {
    const { options } = this.props;
    if (!options.length) return;
    let next = this.state.active;
    for (let i = 0; i < options.length; i += 1) {
      next = (next + step + options.length) % options.length;
      if (!options[next].disabled) break;
    }
    this.setState({ active: next }, this.scrollToActive);
  };

  // type-ahead, as a native select has
  jumpTo = (char) => {
    const { options } = this.props;
    if (this.typeTimer) window.clearTimeout(this.typeTimer);
    this.typed += char.toLowerCase();
    this.typeTimer = window.setTimeout(() => {
      this.typed = "";
    }, 700);

    const from = this.state.active + (this.typed.length > 1 ? 0 : 1);
    for (let i = 0; i < options.length; i += 1) {
      const idx = (from + i) % options.length;
      const label = String(options[idx].label || "").toLowerCase();
      if (!options[idx].disabled && label.startsWith(this.typed)) {
        this.setState({ active: idx }, this.scrollToActive);
        return;
      }
    }
  };

  onKeyDown = (e) => {
    const { open, active } = this.state;
    const { options, disabled } = this.props;
    if (disabled) return;

    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        this.open();
      }
      return;
    }

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        this.close(true);
        break;
      case "Tab":
        // leaving the field commits nothing, as a native select does not
        this.close();
        break;
      case "ArrowDown":
        e.preventDefault();
        this.move(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        this.move(-1);
        break;
      case "Home":
        e.preventDefault();
        this.setState({ active: 0 }, this.scrollToActive);
        break;
      case "End":
        e.preventDefault();
        this.setState({ active: options.length - 1 }, this.scrollToActive);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (options[active]) this.pick(options[active]);
        break;
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) this.jumpTo(e.key);
    }
  };

  render() {
    const { options, value, placeholder, disabled, id, compact } = this.props;
    const { open, active, up, pos } = this.state;

    const at = this.indexOfValue();
    const chosen = at >= 0 ? options[at] : null;

    const menu = open && pos && (
      <ul
        className={`dd-menu ${up ? "is-up" : ""}`}
        id={`${this.id}-list`}
        role="listbox"
        ref={this.list}
        tabIndex={-1}
        style={{
          left: pos.left,
          width: pos.width,
          maxHeight: Math.min(256, pos.room),
          ...(up ? { bottom: window.innerHeight - pos.top } : { top: pos.top }),
        }}
      >
        {/* A list with nothing in it has to say so. The native select showed
            its placeholder option here, so an empty box was all the teacher
            got when a lookup came back empty -- as school stages does until
            the teacher has added any. */}
        {options.length === 0 && (
          <li className="dd-empty" role="presentation">
            {this.props.emptyLabel}
          </li>
        )}

        {options.map((o, i) => {
          const isOn = String(o.value) === String(value);
          return (
            <li
              key={`${o.value}-${i}`}
              id={`${this.id}-opt-${i}`}
              role="option"
              aria-selected={isOn}
              className={`dd-opt ${i === active ? "is-active" : ""} ${isOn ? "is-on" : ""}`}
              // mousedown, not click: the document listener closes the menu on
              // mousedown, and a click would land after it had gone
              onMouseDown={(e) => {
                e.preventDefault();
                this.pick(o);
              }}
              onMouseEnter={() => this.setState({ active: i })}
            >
              <span className="dd-opttx">{o.label}</span>
              {isOn && <Icon name="check" className="dd-tick" />}
            </li>
          );
        })}
      </ul>
    );

    return (
      <div
        className={`dd ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""} ${compact ? "is-compact" : ""}`}
        ref={this.root}
      >
        <button
          type="button"
          id={id}
          className="dd-trigger"
          ref={(el) => (this.trigger = el)}
          onClick={() => (open ? this.close() : this.open())}
          onKeyDown={this.onKeyDown}
          disabled={disabled}
          // the select-only combobox pattern: a plain button does not accept
          // aria-activedescendant, so the active option would never be
          // announced as the arrows move through the list
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? `${this.id}-list` : undefined}
          aria-activedescendant={open && options[active] ? `${this.id}-opt-${active}` : undefined}
        >
          <span className={`dd-value ${chosen ? "" : "is-placeholder"}`}>
            {chosen ? chosen.label : placeholder}
          </span>
          <Icon name="chevron" className="dd-chev" />
        </button>

        {menu && ReactDOM.createPortal(menu, document.body)}
      </div>
    );
  }
}

Dropdown.defaultProps = {
  options: [],
  placeholder: "اختر",
  emptyLabel: "لا توجد اختيارات متاحة",
  disabled: false,
};

export default Dropdown;
